import React, { useState, useEffect } from 'react';
import { withBatchIntegration, useBatch } from '../../Randomness/GameBatchIntegration';
import { mintCardFromBatch } from '../../Randomness/SegmentManager';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Flex,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useToast,
  useDisclosure,
  Progress,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  SimpleGrid,
  Input,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper
} from '@chakra-ui/react';
import { 
  FaInfoCircle, 
  FaGift, 
  FaWallet, 
  FaTrophy, 
  FaLock,
  FaPlus,
  FaMinus,
  FaChartLine,
  FaCoins,
  FaTicketAlt,
  FaCheck,
  FaRandom,
  FaHistory,
  FaArrowLeft,
  FaExclamationTriangle,
  FaUserFriends,
  FaUserAlt,
  FaClock
} from 'react-icons/fa';
import { MEGA_MILLIONS_CONFIG } from '../ScratchCardConfigs';
import MegaMillionsCard from '../../CardTypes/MegaMillionsCard';
import { useBalance } from '../../../../../context/BalanceContext';

/**
 * MegaMillionsGame Component
 * 
 * A standalone component for the Mega Millions game with complete game logic,
 * dark red-themed UI, and dedicated bounty pool.
 */
const MegaMillionsGame = ({ 
  isPreview = false,
  gameState: propGameState = null,
  onBuy,
  onReveal,
  onClose
}) => {
  // Game state management
  const [gameState, setGameState] = useState(propGameState);
  const [gameResult, setGameResult] = useState(null);
  
  // User balance from context
  const { balance, tokens, setTokens } = useBalance();
  const [localBalance, setLocalBalance] = useState(balance.jinu || 1500);
  const userBalance = localBalance;
  
  // Batch information from context
  const { batch, loading: batchLoading } = useBatch();
  
  // UI state
  const toast = useToast();
  const { isOpen, onOpen, onClose: closeModal } = useDisclosure();
  
  // Function to update both local and global balances
  const updateJinuBalance = (amount) => {
    // Update local balance
    setLocalBalance(prev => {
      const newBalance = prev + amount;
      
      // Update global balance in BalanceContext
      balance.jinu = newBalance;
      
      // Update the tokens array
      const updatedTokens = tokens.map(token => 
        token.symbol === 'JINU' ? {...token, balance: newBalance} : token
      );
      setTokens(updatedTokens);
      
      return newBalance;
    });
  };
  
  // Convert prize value string to number (e.g., '5K' to 5000)
  const convertPrizeToNumber = (prizeString) => {
    if (!prizeString) return 0;
    
    const value = prizeString.toString().toUpperCase();
    
    if (value.includes('K')) {
      return parseFloat(value.replace('K', '')) * 1000;
    } else if (value.includes('M')) {
      return parseFloat(value.replace('M', '')) * 1000000;
    } else {
      return parseFloat(value);
    }
  };
  
  // Handle buying a ticket
  const handleBuy = () => {
    startNewGame();
  };

  // Handle revealing winning symbols
  const handleReveal = (index) => {
    // If an index is provided, we can use it for specific cell reveals in the future
    // For now, just reveal all winning symbols
    revealWinningSymbols();
  };

  // Handle gifting a ticket to a friend
  const handleGift = () => {
    toast({
      title: 'Gift Feature Coming Soon',
      description: 'The ability to gift tickets will be available in the next update!',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  };
  
  // Game logic
  const startNewGame = async () => {
    // Check if user has enough balance
    if (userBalance < MEGA_MILLIONS_CONFIG.cost) {
      toast({
        title: 'Insufficient Balance',
        description: `You need at least ${MEGA_MILLIONS_CONFIG.cost} JINU to play.`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Check if batch is available
    if (!batch) {
      toast({
        title: 'No Active Batch',
        description: 'There is no active batch for this game. Please create a batch from the Batch Dashboard.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    
    try {
      // Deduct cost from balance
      updateJinuBalance(-MEGA_MILLIONS_CONFIG.cost);
      
      // Mint a card from the batch
      const card = await mintCardFromBatch(batch.id);
      
      console.log('Minted card from batch:', card);
      
      // Generate cell values based on the card's seed
      const cardSeed = parseInt(card.id.substring(card.id.length - 8), 16);
      const seedRandom = (seed) => {
        const x = Math.sin(seed) * 10000;
        return x - Math.floor(x);
      };
      
      const cellValues = Array(20).fill().map((_, index) => {
        const randomValue = Math.floor(seedRandom(cardSeed + index) * 100) + 1;
        return randomValue;
      });
      
      // Determine if this is a winning game based on the card's prize
      const isWinning = card.prize && card.prize.amount > 0;
      
      // If winning, select 3 random cells to be winning cells
      let winningCells = [];
      if (isWinning) {
        // Select 3 random cells to be winning using the card's seed
        while (winningCells.length < 3) {
          const randomCell = Math.floor(seedRandom(cardSeed + 100 + winningCells.length) * 20);
          if (!winningCells.includes(randomCell)) {
            winningCells.push(randomCell);
          }
        }
      }
      
      // Get prize information from the card
      const prizeValue = isWinning ? `${card.prize.amount}` : '0';
      const prizeAmount = isWinning ? card.prize.amount : 0;
      
      // Create new game state
      const newGameState = {
      id: `game-${Date.now()}`,
      status: 'active',
      cellValues,
      winningCells,
      isWinning,
      prizeValue,
      prizeAmount,
      revealedCells: [],
      winningSymbolsRevealed: false
    };
    
      setGameState(newGameState);
      
      // If this is handled by an external component
      if (onBuy) {
        onBuy(newGameState);
      }
    } catch (error) {
      console.error('Error starting new game:', error);
      toast({
        title: 'Game Error',
        description: 'There was an error starting a new game. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      
      // Refund the cost if there was an error
      updateJinuBalance(MEGA_MILLIONS_CONFIG.cost);
    }
  };
  
  // Handle revealing winning symbols
  const revealWinningSymbols = () => {
    if (!gameState || gameState.status !== 'active') return;
    
    setGameState(prev => ({
      ...prev,
      winningSymbolsRevealed: true
    }));
    
    // If this is handled by an external component
    if (onReveal) {
      onReveal();
    }
  };
  
  // Handle game completion
  const completeGame = () => {
    if (!gameState || gameState.status !== 'active') return;
    
    // Check if player won
    if (gameState.isWinning) {
      // Add prize to balance
      updateJinuBalance(gameState.prizeAmount);
      
      // Show success toast
      toast({
        title: 'Congratulations!',
        description: `You won ${gameState.prizeAmount} JINU!`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      // Set game result
      setGameResult({
        won: true,
        amount: gameState.prizeAmount
      });
    } else {
      // Show better luck next time toast
      toast({
        title: 'Better Luck Next Time',
        description: 'You didn\'t win this time. Try again!',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      
      // Set game result
      setGameResult({
        won: false,
        amount: 0
      });
    }
    
    // Update game state
    setGameState(prev => ({
      ...prev,
      status: 'completed'
    }));
    
    // Open result modal
    onOpen();
  };
  
  // Handle play again
  const handlePlayAgain = () => {
    // Close modal
    closeModal();
    
    // Reset game state
    setGameState(null);
    setGameResult(null);
    
    // Start new game
    startNewGame();
  };
  
  // Auto-start game if no game state is provided
  useEffect(() => {
    if (!gameState && !isPreview) {
      startNewGame();
    }
  }, []);
  
  // Theme colors for Mega Millions (lime green theme)
  const colors = {
    primary: '#32CD32', // Lime green background
    secondary: '#228B22', // Darker lime green for containers
    accent: '#7CFC00', // Light lime accent
    text: 'white',
    button: 'green',
    buttonVariant: 'solid',
    progressColor: 'green.400'
  };
  
  // Render preview mode
  if (isPreview) {
    return (
      <Box>
        <MegaMillionsCard isPreview={true} />
      </Box>
    );
  }
  
  return (
    <Box width="100%" minHeight="auto" p={4}>
      {/* Game Card and Controls - Top Section */}
      <Flex direction={{ base: 'column', md: 'row' }} width="100%" gap={4} mb={4}>
        {/* Left column - Card */}
        <Box width={{ base: "100%", md: "400px" }} maxWidth="400px">
          <MegaMillionsCard 
            isPreview={isPreview}
            gameState={gameState}
            onReveal={handleReveal}
          />
        </Box>
        
        {/* Right column - Game Controls */}
        <Box width={{ base: "100%", md: "400px" }} maxWidth="400px">
          <Box 
            bg="linear-gradient(145deg, #660000, #8B0000)" 
            p={4} 
            borderRadius="lg"
            width="100%"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.5)"
            border="1px solid rgba(220, 20, 60, 0.3)"
            backdropFilter="blur(10px)"
          >
            <Heading size="md" color="#DC143C" mb={4}>Game Controls</Heading>
            
            {/* Buy and Send Gift buttons side by side */}
            <HStack spacing={4} mb={4}>
              <Button 
                colorScheme="yellow" 
                size="lg"
                width="100%"
                onClick={handleBuy}
                isDisabled={gameState && gameState.status === 'active'}
              >
                Buy ({MEGA_MILLIONS_CONFIG.cost} JINU)
              </Button>
              
              <Button 
                colorScheme="red" 
                size="lg"
                width="100%"
                onClick={handleGift}
                leftIcon={<FaGift />}
              >
                Send As Gift
              </Button>
            </HStack>
            
            {/* Reveal button */}
            <Button 
              colorScheme="green" 
              size="lg"
              width="100%"
              mb={6}
              onClick={() => handleReveal(0)}
              isDisabled={!gameState || gameState.status !== 'active' || gameState.winningSymbolsRevealed}
            >
              Reveal Winning Symbols
            </Button>
            
            {/* How to Play */}
            <Box mb={4}>
              <Heading size="sm" color="green.300" mb={2} display="flex" alignItems="center">
                <Icon as={FaInfoCircle} mr={2} />
                How To Play:
              </Heading>
              
              <Text color="white" fontSize="sm">
                Click on each square to reveal symbols. Match 5 of the winning symbols shown above to win their prize value.
              </Text>
            </Box>
          </Box>
        </Box>
        
        {/* Right column - Create Your Game */}
        <Box width={{ base: "100%", md: "400px" }} maxWidth="400px">
          <Box 
            bg="linear-gradient(145deg, #660000, #8B0000)" 
            p={4} 
            borderRadius="lg"
            width="100%"
            height="100%"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.5)"
            border="1px solid rgba(220, 20, 60, 0.3)"
            backdropFilter="blur(10px)"
          >
            <Heading size="md" color="#DC143C" mb={4}>Create Your Game</Heading>
            
            {/* Card Name */}
            <FormControl mb={4}>
              <FormLabel color="#DC143C">Card Name</FormLabel>
              <Input 
                placeholder="e.g. ROYAL FLUSH" 
                bg="rgba(0,0,0,0.2)" 
                borderColor="rgba(220, 20, 60, 0.3)"
                _hover={{ borderColor: "#DC143C" }}
                color="white"
              />
            </FormControl>
            
            {/* Cost */}
            <FormControl mb={4}>
              <FormLabel color="#DC143C">Cost (JINU)</FormLabel>
              <Slider 
                defaultValue={30} 
                min={10} 
                max={100}
                step={5}
                colorScheme="red"
              >
                <SliderTrack bg="rgba(0,0,0,0.3)">
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb boxSize={6} />
              </Slider>
            </FormControl>
            
            {/* Number of Cards */}
            <FormControl mb={4}>
              <FormLabel color="#DC143C">Number of Cards</FormLabel>
              <Slider 
                defaultValue={50} 
                min={10} 
                max={100}
                step={10}
                colorScheme="red"
              >
                <SliderTrack bg="rgba(0,0,0,0.3)">
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb boxSize={6} />
              </Slider>
              <Text color="gray.400" fontSize="xs" mt={1}>Maximum 5000 cards</Text>
            </FormControl>
            
            {/* Build Your Prize Pool */}
            <Box mb={4}>
              <Heading size="sm" color="#DC143C" mb={2}>Build Your Prize Pool</Heading>
              <Text color="white" fontSize="sm" mb={3}>
                Add tokens and NFTs from your wallet to create prizes:
              </Text>
              
              <VStack align="flex-start" spacing={1} color="white" fontSize="sm">
                <Text>• 25k JINU - 1 prize</Text>
                <Text>• 5k JINU - 5 prizes</Text>
                <Text>• NFTs - 10 prizes</Text>
              </VStack>
            </Box>
            
            {/* Create Game Button */}
            <Button 
              colorScheme="red" 
              size="lg" 
              width="100%"
              mt={4}
            >
              Create Game
            </Button>
          </Box>
        </Box>
      </Flex>
      
      {/* Bounty Pool Section */}
      <Box 
        width="100%" 
        bg="rgba(59, 15, 15, 0.95)" 
        p={5} 
        borderRadius="lg"
        mb={4}
        boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.5)"
        border="1px solid rgba(200, 104, 104, 0.15)"
        backdropFilter="blur(10px)"
      >
        <Heading size="lg" color="red.300" mb={6} textAlign="center" display="flex" alignItems="center" justifyContent="center">
          <Icon as={FaTrophy} mr={2} color="yellow.400" />
          Mega Millions Bounty Pool
        </Heading>
        
        {/* Stats Grid */}
        <SimpleGrid columns={{ base: 1, sm: 3, md: 5 }} spacing={4} mb={6}>
          {/* Overall RTP */}
          <Box 
            bg="rgba(59, 15, 15, 0.8)" 
            p={3} 
            borderRadius="md" 
            textAlign="center"
            border="1px solid rgba(200, 104, 104, 0.3)"
          >
            <Flex direction="column" align="center">
              <Icon as={FaChartLine} color="red.300" boxSize={6} mb={2} />
              <Text color="red.300" fontSize="sm" fontWeight="bold">Overall RTP</Text>
              <Text color="white" fontWeight="bold" fontSize="2xl">90%</Text>
              <Text color="red.300" fontSize="xs">70% PRIZE DIST</Text>
              <Text color="red.300" fontSize="xs">+15% BONUSES</Text>
            </Flex>
          </Box>

          {/* Prize Pool */}
          <Box 
            bg="rgba(59, 15, 15, 0.8)" 
            p={3} 
            borderRadius="md" 
            textAlign="center"
            border="1px solid rgba(200, 104, 104, 0.3)"
          >
            <Flex direction="column" align="center">
              <Icon as={FaCoins} color="yellow.400" boxSize={6} mb={2} />
              <Text color="red.300" fontSize="sm" fontWeight="bold">Prize Pool</Text>
              <Text color="white" fontWeight="bold" fontSize="2xl">2,500,000 JINU</Text>
              <Text color="yellow.400" fontSize="xs">+25,000 JINU</Text>
            </Flex>
          </Box>

          {/* Cards Minted */}
          <Box 
            bg="rgba(59, 15, 15, 0.8)" 
            p={3} 
            borderRadius="md" 
            textAlign="center"
            border="1px solid rgba(200, 104, 104, 0.3)"
          >
            <Flex direction="column" align="center">
              <Icon as={FaTicketAlt} color="blue.300" boxSize={6} mb={2} />
              <Text color="red.300" fontSize="sm" fontWeight="bold">Cards Minted</Text>
              <Text color="white" fontWeight="bold" fontSize="2xl">2,500</Text>
              <Text color="blue.300" fontSize="xs">7,500 remaining</Text>
            </Flex>
          </Box>

          {/* Prizes Claimed */}
          <Box 
            bg="rgba(59, 15, 15, 0.8)" 
            p={3} 
            borderRadius="md" 
            textAlign="center"
            border="1px solid rgba(200, 104, 104, 0.3)"
          >
            <Flex direction="column" align="center">
              <Icon as={FaGift} color="red.300" boxSize={6} mb={2} />
              <Text color="red.300" fontSize="sm" fontWeight="bold">Prizes Claimed</Text>
              <Text color="white" fontWeight="bold" fontSize="2xl">375,000 JINU</Text>
              <Text color="green.300" fontSize="xs">+632 winners</Text>
            </Flex>
          </Box>

          {/* Top Prizes Left */}
          <Box 
            bg="rgba(59, 15, 15, 0.8)" 
            p={3} 
            borderRadius="md" 
            textAlign="center"
            border="1px solid rgba(200, 104, 104, 0.3)"
          >
            <Flex direction="column" align="center">
              <Icon as={FaTrophy} color="yellow.400" boxSize={6} mb={2} />
              <Text color="red.300" fontSize="sm" fontWeight="bold">Top Prizes Left</Text>
              <Text color="white" fontWeight="bold" fontSize="2xl">9</Text>
              <Text color="yellow.400" fontSize="xs">1x 100K JINU</Text>
              <Text color="yellow.400" fontSize="xs">4x 50K JINU</Text>
            </Flex>
          </Box>
        </SimpleGrid>
        
        {/* Progress Bars */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
          {/* Minting Progress */}
          <Box 
            bg="rgba(59, 15, 15, 0.8)" 
            p={3} 
            borderRadius="md" 
            border="1px solid rgba(200, 104, 104, 0.3)"
          >
            <Text color="red.300" fontSize="sm" fontWeight="bold" mb={2}>Minting Progress</Text>
            <Text color="white" fontSize="sm" mb={1}>2,500 minted</Text>
            <Progress 
              value={25} 
              colorScheme="blue" 
              size="sm" 
              bg="rgba(255, 255, 255, 0.1)" 
              borderRadius="full"
              mb={1}
            />
            <Flex justify="space-between">
              <Text color="gray.400" fontSize="xs">0</Text>
              <Text color="red.300" fontSize="xs">10,000 total</Text>
            </Flex>
          </Box>
          
          {/* Prize Claims Progress */}
          <Box 
            bg="rgba(59, 15, 15, 0.8)" 
            p={3} 
            borderRadius="md" 
            border="1px solid rgba(200, 104, 104, 0.3)"
          >
            <Text color="red.300" fontSize="sm" fontWeight="bold" mb={2}>Prize Claims Progress</Text>
            <Text color="white" fontSize="sm" mb={1}>375,000 JINU claimed</Text>
            <Progress 
              value={25} 
              colorScheme="green" 
              size="sm" 
              bg="rgba(255, 255, 255, 0.1)" 
              borderRadius="full"
              mb={1}
            />
            <Flex justify="space-between">
              <Text color="gray.400" fontSize="xs">0</Text>
              <Text color="red.300" fontSize="xs">1,500,000 JINU total</Text>
            </Flex>
          </Box>
        </SimpleGrid>
        
        {/* Prize Distribution and Recent Claims */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
          {/* Prize Distribution */}
          <Box 
            bg="rgba(59, 15, 15, 0.8)" 
            p={3} 
            borderRadius="md" 
            border="1px solid rgba(200, 104, 104, 0.3)"
          >
            <Text color="red.300" fontSize="sm" fontWeight="bold" mb={2}>Prize Distribution</Text>
            <Table size="sm" variant="unstyled">
              <Thead>
                <Tr>
                  <Th color="red.200" fontSize="xs">PRIZE</Th>
                  <Th color="red.200" fontSize="xs">TOTAL</Th>
                  <Th color="red.200" fontSize="xs">CLAIMED</Th>
                  <Th color="red.200" fontSize="xs">REMAIN</Th>
                  <Th color="red.200" fontSize="xs">ODDS</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td>
                    <Box 
                      bg="linear-gradient(90deg, rgba(234, 51, 51, 0.8) 0%, rgba(206, 34, 34, 0.8) 100%)" 
                      borderRadius="md" 
                      py={1} 
                      px={3} 
                      display="inline-block"
                      boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                      border="0.75px solid rgba(255, 255, 255, 0.5)"
                      backdropFilter="blur(4px)"
                      minWidth="120px"
                      textAlign="center"
                      whiteSpace="nowrap"
                    >
                      <Text color="white" fontWeight="extrabold" fontSize="sm" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">500,000 JINU</Text>
                    </Box>
                  </Td>
                  <Td color="white">1</Td>
                  <Td color="red.400">0</Td>
                  <Td color="red.300">1</Td>
                  <Td color="yellow.300">1 in 10k</Td>
                </Tr>
                <Tr>
                  <Td>
                    <Box 
                      bg="linear-gradient(90deg, rgba(237, 58, 58, 0.8) 0%, rgba(217, 40, 40, 0.8) 100%)" 
                      borderRadius="md" 
                      py={1} 
                      px={3} 
                      display="inline-block"
                      boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                      border="0.75px solid rgba(255, 255, 255, 0.5)"
                      backdropFilter="blur(4px)"
                      minWidth="120px"
                      textAlign="center"
                      whiteSpace="nowrap"
                    >
                      <Text color="white" fontWeight="extrabold" fontSize="sm" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">250,000 JINU</Text>
                    </Box>
                  </Td>
                  <Td color="white">2</Td>
                  <Td color="red.400">0</Td>
                  <Td color="red.300">2</Td>
                  <Td color="yellow.300">1 in 5k</Td>
                </Tr>
                <Tr>
                  <Td>
                    <Box 
                      bg="linear-gradient(90deg, rgba(229, 70, 70, 0.8) 0%, rgba(202, 56, 56, 0.8) 100%)" 
                      borderRadius="md" 
                      py={1} 
                      px={3} 
                      display="inline-block"
                      boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                      border="0.75px solid rgba(255, 255, 255, 0.5)"
                      backdropFilter="blur(4px)"
                      minWidth="120px"
                      textAlign="center"
                      whiteSpace="nowrap"
                    >
                      <Text color="white" fontWeight="extrabold" fontSize="sm" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">100,000 JINU</Text>
                    </Box>
                  </Td>
                  <Td color="white">4</Td>
                  <Td color="red.400">1</Td>
                  <Td color="red.300">3</Td>
                  <Td color="yellow.300">1 in 2.5k</Td>
                </Tr>
                <Tr>
                  <Td>
                    <Box 
                      bg="linear-gradient(90deg, rgba(245, 102, 102, 0.8) 0%, rgba(221, 85, 85, 0.8) 100%)" 
                      borderRadius="md" 
                      py={1} 
                      px={3} 
                      display="inline-block"
                      boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                      border="0.75px solid rgba(255, 255, 255, 0.5)"
                      backdropFilter="blur(4px)"
                      minWidth="120px"
                      textAlign="center"
                      whiteSpace="nowrap"
                    >
                      <Text color="white" fontWeight="extrabold" fontSize="sm" letterSpacing="tight" textShadow="0 2px 5px rgba(0, 0, 0, 0.7)">50,000 JINU</Text>
                    </Box>
                  </Td>
                  <Td color="white">10</Td>
                  <Td color="red.400">0</Td>
                  <Td color="red.300">10</Td>
                  <Td color="yellow.300">1 in 1k</Td>
                </Tr>
              </Tbody>
            </Table>
          </Box>
          
          {/* Recent Claims */}
          <Box 
            bg="rgba(59, 15, 15, 0.8)" 
            p={3} 
            borderRadius="md" 
            border="1px solid rgba(200, 104, 104, 0.3)"
          >
            <Text color="red.300" fontSize="sm" fontWeight="bold" mb={2}>Recent Claims</Text>
            <Table size="sm" variant="unstyled">
              <Thead>
                <Tr>
                  <Th color="red.200" fontSize="xs">PLAYER</Th>
                  <Th color="red.200" fontSize="xs">PRIZE</Th>
                  <Th color="red.200" fontSize="xs" isNumeric>TIME</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td color="white">0x3b...9c2e</Td>
                  <Td>
                    <Box 
                      bg="linear-gradient(90deg, rgba(0, 128, 255, 0.8) 0%, rgba(0, 100, 200, 0.8) 100%)" 
                      borderRadius="md" 
                      py={1} 
                      px={2} 
                      display="inline-block"
                      boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                      border="0.75px solid rgba(255, 255, 255, 0.5)"
                    >
                      <Text color="white" fontWeight="bold" fontSize="xs">10,000 JINU</Text>
                    </Box>
                  </Td>
                  <Td color="white" isNumeric>2 minutes ago</Td>
                </Tr>
                <Tr>
                  <Td color="white">0xc4...7d1e</Td>
                  <Td>
                    <Box 
                      bg="linear-gradient(90deg, rgba(255, 165, 0, 0.8) 0%, rgba(200, 130, 0, 0.8) 100%)" 
                      borderRadius="md" 
                      py={1} 
                      px={2} 
                      display="inline-block"
                      boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                      border="0.75px solid rgba(255, 255, 255, 0.5)"
                    >
                      <Text color="white" fontWeight="bold" fontSize="xs">500 JINU</Text>
                    </Box>
                  </Td>
                  <Td color="white" isNumeric>5 minutes ago</Td>
                </Tr>
                <Tr>
                  <Td color="white">0x2b...59dc</Td>
                  <Td>
                    <Box 
                      bg="linear-gradient(90deg, rgba(200, 0, 0, 0.8) 0%, rgba(150, 0, 0, 0.8) 100%)" 
                      borderRadius="md" 
                      py={1} 
                      px={2} 
                      display="inline-block"
                      boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                      border="0.75px solid rgba(255, 255, 255, 0.5)"
                    >
                      <Text color="white" fontWeight="bold" fontSize="xs">2,000 JINU</Text>
                    </Box>
                  </Td>
                  <Td color="white" isNumeric>8 minutes ago</Td>
                </Tr>
                <Tr>
                  <Td color="white">0x4d...7c7f</Td>
                  <Td>
                    <Box 
                      bg="linear-gradient(90deg, rgba(200, 0, 0, 0.8) 0%, rgba(150, 0, 0, 0.8) 100%)" 
                      borderRadius="md" 
                      py={1} 
                      px={2} 
                      display="inline-block"
                      boxShadow="0 0 4px rgba(255, 255, 255, 0.15), 0 0 15px rgba(255, 255, 255, 0.1)"
                      border="0.75px solid rgba(255, 255, 255, 0.5)"
                    >
                      <Text color="white" fontWeight="bold" fontSize="xs">5,000 JINU</Text>
                    </Box>
                  </Td>
                  <Td color="white" isNumeric>12 minutes ago</Td>
                </Tr>
              </Tbody>
            </Table>
          </Box>
        </SimpleGrid>
        
        {/* Prize Tiers and Win Odds */}
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6} mb={6}>
          {/* Top Prizes */}
          <Box>
            <Text color="red.300" fontSize="sm" fontWeight="bold" mb={2}>Top Prizes</Text>
            <Box 
              bg="rgba(59, 15, 15, 0.8)" 
              p={3} 
              borderRadius="md" 
              textAlign="center"
              border="1px solid rgba(200, 104, 104, 0.3)"
            >
              <Text color="white" fontWeight="bold">12 cards</Text>
              <Text color="yellow.400">350,000 JINU</Text>
            </Box>
          </Box>
          
          {/* Mid-tier Prizes */}
          <Box>
            <Text color="red.300" fontSize="sm" fontWeight="bold" mb={2}>Mid-tier Prizes</Text>
            <Box 
              bg="rgba(59, 15, 15, 0.8)" 
              p={3} 
              borderRadius="md" 
              textAlign="center"
              border="1px solid rgba(200, 104, 104, 0.3)"
            >
              <Text color="white" fontWeight="bold">420 cards</Text>
              <Text color="yellow.400">265,000 JINU</Text>
            </Box>
          </Box>
          
          {/* Lower Prizes */}
          <Box>
            <Text color="red.300" fontSize="sm" fontWeight="bold" mb={2}>Lower Prizes</Text>
            <Box 
              bg="rgba(59, 15, 15, 0.8)" 
              p={3} 
              borderRadius="md" 
              textAlign="center"
              border="1px solid rgba(200, 104, 104, 0.3)"
            >
              <Text color="white" fontWeight="bold">4200 cards</Text>
              <Text color="yellow.400">720,000 JINU</Text>
            </Box>
          </Box>
          
          {/* Win Odds */}
          <Box>
            <Text color="red.300" fontSize="sm" fontWeight="bold" mb={2}>Win Odds</Text>
            <Box 
              bg="rgba(59, 15, 15, 0.8)" 
              p={3} 
              borderRadius="md" 
              textAlign="center"
              border="1px solid rgba(200, 104, 104, 0.3)"
            >
              <Text color="white" fontWeight="bold">1 in 2.15</Text>
              <Text color="yellow.400">Top Prize 1 in 10,000</Text>
            </Box>
          </Box>
        </SimpleGrid>
      </Box>
      
      {/* Randomness Verification */}
      <Box 
        width="100%" 
        bg="rgba(59, 15, 15, 0.95)" 
        p={5} 
        borderRadius="lg"
        mb={4}
        boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.5)"
        border="1px solid rgba(200, 104, 104, 0.15)"
        backdropFilter="blur(10px)"
      >
        <Flex direction={{ base: 'column', md: 'row' }} gap={6}>
          <Box flex="1">
            <Heading size="sm" color="red.300" mb={2} display="flex" alignItems="center">
              <Icon as={FaRandom} mr={2} color="red.300" />
              Randomness Verification
            </Heading>
            <Text color="white" fontSize="sm">
              All game outcomes are determined using verifiable random functions on the blockchain. This ensures complete transparency and fairness for all players.
            </Text>
            <Button size="sm" colorScheme="red" variant="outline" mt={2} leftIcon={<FaCheck />}>
              Verification Available On-Chain
            </Button>
          </Box>
          
          <Box flex="1">
            <Heading size="sm" color="red.300" mb={2} display="flex" alignItems="center">
              <Icon as={FaHistory} mr={2} color="red.300" />
              Latest Block Hash
            </Heading>
            <Text color="gray.300" fontSize="xs" mb={1}>
              0xf331f7c0d43e9c3e7d34d8c7f7c9a5f4c3f1c
            </Text>
            <Text color="gray.400" fontSize="xs">
              Block #16,468,532 · Updated: 2 mins ago
            </Text>
          </Box>
        </Flex>
      </Box>
      
      {/* Empty placeholder - the main content is now in the 3-column layout above */}
      
      {/* Result Modal */}
      <Modal isOpen={isOpen} onClose={closeModal} isCentered size="md">
        <ModalOverlay backdropFilter="blur(10px)" />
        <ModalContent bg={colors.secondary} borderRadius="xl" borderWidth="1px" borderColor="rgba(255,255,255,0.1)">
          <ModalHeader color={colors.text} textAlign="center">
            {gameResult?.won ? 'Congratulations! 🎉' : 'Better Luck Next Time 🍀'}
          </ModalHeader>
          <ModalBody>
            <VStack spacing={4} align="center" py={4}>
              {gameResult?.won ? (
                <>
                  <Icon as={FaTrophy} color="yellow.400" boxSize={16} />
                  <Heading size="lg" color="yellow.400">
                    You Won {gameResult.amount.toLocaleString()} JINU!
                  </Heading>
                  <Text color={colors.text} textAlign="center">
                    The prize has been added to your balance. Keep playing for more chances to win!
                  </Text>
                </>
              ) : (
                <>
                  <Icon as={FaTicketAlt} color="gray.400" boxSize={16} />
                  <Heading size="lg" color={colors.text}>
                    No Match Found
                  </Heading>
                  <Text color={colors.text} textAlign="center">
                    Don't give up! Try again for a chance to win up to 100,000,000 JINU!
                  </Text>
                </>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" mr={3} onClick={handlePlayAgain} width="100%">
              Play Again ({MEGA_MILLIONS_CONFIG.cost} JINU)
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// Wrap the component with batch integration
export default withBatchIntegration(MegaMillionsGame);
